import { createSlice } from '@reduxjs/toolkit'
import dayjs from 'dayjs'

import { absenceApi } from '@core/app/api/absenceApi'
import { RootState } from '@core/app/store/store'
import { AbsenceData, EmployeeSettings } from '@core/app/types/absenceApiType'
import { CalendarHoliday, CalendarTrip, Job, LoggedWorkByIdData, LoggedWorkItem } from '@core/app/types/logworkApiType'

import { ShiftData } from '@page/logwork/components/Shifts/types'

interface AbsenceState {
    currentYear: number
    view: 'view' | 'create'
    absenceData: AbsenceData | null
    absenceDays: LoggedWorkItem[]
    employeeSettings: EmployeeSettings | null
    jobs: Job[]
    sJobs: {
        invoiceCurrencyId: number
        jobId: number
        name: string
        salaryCurrencyId: number
    }[]
    absenceById: LoggedWorkByIdData | null
    loading: boolean
    successDataLoaded: boolean
    trips: CalendarTrip[]
    holidays: CalendarHoliday[]
    jobShifts: ShiftData[]
}

const initialState: AbsenceState = {
    currentYear: dayjs().year(),
    view: (localStorage.getItem('viewState') as 'view' | 'create') || 'view',
    absenceData: null,
    absenceDays: [],
    jobs: [],
    sJobs: [],
    trips: [],
    holidays: [],
    absenceById: null,
    successDataLoaded: false,
    loading: false,
    employeeSettings: null,
    jobShifts: [],
}

const absenceSlice = createSlice({
    name: 'absence',
    initialState,
    reducers: {
        setCurrentYear(state, action) {
            state.currentYear = Number(action.payload)
        },
        createAbsence(state) {
            state.view = 'create'
            state.jobs = []
            localStorage.setItem('viewState', 'create')
        },
        closeAbsence(state) {
            state.view = 'view'
            localStorage.setItem('viewState', 'view')
        },
    },
    extraReducers(builder) {
        builder.addMatcher(absenceApi.endpoints.getData.matchPending, (state) => {
            state.loading = true
        })
        builder.addMatcher(absenceApi.endpoints.getData.matchRejected, (state) => {
            state.loading = false
        })
        builder.addMatcher(absenceApi.endpoints.getData.matchFulfilled, (state, action) => {
            const { absenceData, work, jobs, trips, nationalHolidays, employeeSettings, shifts } = action.payload
            const isAbsenceListRequest = Boolean(action.meta.arg.originalArgs?.workArticleIds)

            if (absenceData) {
                state.absenceData = action.payload.absenceData
            }
            if (work?.loggedWork && isAbsenceListRequest) {
                state.absenceDays = Object.values(work.loggedWork).flat()
                state.sJobs = work?.jobs
            }
            if (jobs !== undefined) {
                state.jobs = action.payload.jobs || []
            }
            if (trips) {
                state.trips = action.payload.trips
            }
            if (nationalHolidays) {
                state.holidays = action.payload.nationalHolidays
            }
            if (employeeSettings) {
                state.employeeSettings = employeeSettings
            }
            if (shifts !== undefined) {
                state.jobShifts = [...(shifts || []).filter((shift) => shift.logged === 0)]
            }

            state.loading = false

            if (jobs && jobs.length > 0) {
                state.successDataLoaded = true
            } else {
                state.successDataLoaded = false
            }
        }),
            builder.addMatcher(absenceApi.endpoints.getAbsenceById.matchFulfilled, (state, action) => {
                state.absenceById = action.payload
            })
    },
})

export default absenceSlice.reducer
export const { setCurrentYear, createAbsence, closeAbsence } = absenceSlice.actions
export const storeCurrentYear = (state: RootState) => state.absence.currentYear
export const storeAbsenceData = (state: RootState) => state.absence.absenceData
export const storeAbsenceLoading = (state: RootState) => state.absence.loading
export const storeAbsenceDays = (state: RootState) => state.absence.absenceDays
export const storeAbsenceById = (state: RootState) => state.absence.absenceById
export const storeJobs = (state: RootState) => state.absence.jobs
export const storeView = (state: RootState) => state.absence.view
export const storeTrips = (state: RootState) => state.absence.trips
export const storeHolidays = (state: RootState) => state.absence.holidays
export const storeSuccessDataLoaded = (state: RootState) => state.absence.successDataLoaded
export const storeEmployeeSettings = (state: RootState) => state.absence.employeeSettings
export const storeSJobs = (state: RootState) => state.absence.sJobs
export const storeJobShifts = (state: RootState) => state.absence.jobShifts
